export interface SlideSelectModifiers {
  additive?: boolean;
  range?: boolean;
}

export interface SlideSelectionState {
  selectedIds: string[];
  activeId: string;
  rangeAnchorId: string;
}

export function nextSlideSelection(
  orderedIds: string[],
  currentSelected: string[],
  activeId: string | null,
  clickedId: string,
  modifiers: SlideSelectModifiers,
  rangeAnchorId: string | null,
): SlideSelectionState {
  if (!orderedIds.includes(clickedId)) {
    const fallback = activeId && orderedIds.includes(activeId) ? activeId : orderedIds[0] || clickedId;
    return {
      selectedIds: fallback ? [fallback] : [],
      activeId: fallback,
      rangeAnchorId: fallback,
    };
  }

  if (modifiers.range) {
    const from = (rangeAnchorId && orderedIds.includes(rangeAnchorId) && rangeAnchorId)
      || (activeId && orderedIds.includes(activeId) && activeId)
      || clickedId;
    const start = orderedIds.indexOf(from);
    const end = orderedIds.indexOf(clickedId);
    const [lo, hi] = start < end ? [start, end] : [end, start];
    return {
      selectedIds: orderedIds.slice(lo, hi + 1),
      activeId: clickedId,
      rangeAnchorId: from,
    };
  }

  if (modifiers.additive) {
    const base = currentSelected.some((id) => orderedIds.includes(id))
      ? currentSelected.filter((id) => orderedIds.includes(id))
      : (activeId && orderedIds.includes(activeId) ? [activeId] : []);
    const exists = base.includes(clickedId);
    const next = exists
      ? base.filter((id) => id !== clickedId)
      : [...base, clickedId];
    if (!next.length) {
      return { selectedIds: [clickedId], activeId: clickedId, rangeAnchorId: clickedId };
    }
    const nextActive = exists
      ? (activeId && next.includes(activeId) ? activeId : next[next.length - 1])
      : clickedId;
    return { selectedIds: next, activeId: nextActive, rangeAnchorId: clickedId };
  }

  return { selectedIds: [clickedId], activeId: clickedId, rangeAnchorId: clickedId };
}

export function slideIdsForDeleteAction(
  orderedIds: string[],
  selectedIds: string[],
  requestedId: string,
): string[] {
  if (selectedIds.includes(requestedId) && selectedIds.length > 1) {
    return orderedIds.filter((id) => selectedIds.includes(id));
  }
  return orderedIds.includes(requestedId) ? [requestedId] : [];
}

export function guardedSlideDeleteIds(
  orderedIds: string[],
  requestedIds: string[],
  lockedIds: Iterable<string>,
): { ids: string[]; reason: 'ok' | 'locked' | 'keepLast' } {
  const locked = new Set(lockedIds);
  const unlocked = requestedIds.filter((id) => orderedIds.includes(id) && !locked.has(id));
  if (!unlocked.length) return { ids: [], reason: 'locked' };
  const remaining = orderedIds.filter((id) => !unlocked.includes(id));
  if (remaining.length < 1) return { ids: [], reason: 'keepLast' };
  return { ids: unlocked, reason: 'ok' };
}
